import { useMemo, useState } from "react";
import Select, { GroupBase, OptionsOrGroups } from "react-select";
import { SquiggleItem } from "../types/types";
import { makeHierarchy, mapHierarchy } from "../biz/itemUtil";

interface ItemOption {
  value: string;
  label: string;
  isDisabled: boolean;
  level: number;
}

const groupLabels = {
  "quest": "QUESTS",
  "personororg": "PERSON/GROUP",
  "place": "PLACES",
  "miscitem": "MISC ITEM",
  "logentry": "LOG ENTRY"
};

const groupColors = {
  "UNKNOWN": "#888",
  "QUESTS": "#284",
  "PERSON/GROUP": "#d92",
  "PLACES": "#b54",
  "MISC ITEM": "#58a",
  "LOG ENTRY": "#ccc"
};

function ItemSelector(
  {
    availableItems,
    selectedItemId,
    onChange,
    hiddenNone,
    className,
    id,
    noneText,
    emptyParentName,
    sizeAfterContent
  } : {
    availableItems: SquiggleItem[],
    selectedItemId: string,
    onChange: (itemId: string) => void,
    hiddenNone?: boolean,
    className?: string,
    id?: string,
    noneText?: string,
    emptyParentName?: string,
    sizeAfterContent?: boolean
  }
) {
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const options = useMemo(() => {
    const itemsPerType = [
      { type: 'quest', items: [] as SquiggleItem[] },
      { type: 'personororg', items: [] as SquiggleItem[] },
      { type: 'place', items: [] as SquiggleItem[] },
      { type: 'miscitem', items: [] as SquiggleItem[] },
      { type: 'logentry', items: [] as SquiggleItem[] },
    ];
    availableItems.forEach(item => {
      itemsPerType.find(t => t.type === item.type)?.items.push(item);
    });

    if (isSearching) {
      return itemsPerType
        .filter(group => group.items.length > 0)
        .map(group => ({
          label: groupLabels[group.type as keyof typeof groupLabels] ?? group.type.toUpperCase(),
          options: group.items
            .map(item => ({
              value: item.id,
              label: item.name,
              isDisabled: false,
              level: 0
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        }));
    }

    const hierarchyPerType = [
      { heading: groupLabels["quest"], items: makeHierarchy(itemsPerType[0].items) },
      { heading: groupLabels["personororg"], items: makeHierarchy(itemsPerType[1].items) },
      { heading: groupLabels["place"], items: makeHierarchy(itemsPerType[2].items) },
      { heading: groupLabels["miscitem"], items: makeHierarchy(itemsPerType[3].items) },
      { heading: groupLabels["logentry"], items: makeHierarchy(itemsPerType[4].items) },
    ];

    let groupedOptions: OptionsOrGroups<ItemOption, GroupBase<ItemOption>> = hierarchyPerType
      .filter(group => group.items.length > 0)
      .map(group => ({
        label: group.heading,
        options: mapHierarchy<ItemOption>(group.items, (item, level) => ({
          value: item?.id ?? "disabled",
          label: item?.name ?? emptyParentName ?? "",
          isDisabled: !item,
          level
        }))
      }));

    if (!hiddenNone) {
      groupedOptions = [{
        label: noneText ?? "",
        options: [{ value: "", label: noneText ?? "", isDisabled: false, level: 0 }]
      }, ...groupedOptions];
    }

    return groupedOptions;
  }, [isSearching, availableItems, hiddenNone, noneText, emptyParentName]);

  const selectedOption = useMemo(() => {
    return options.flatMap(group => (group as GroupBase<ItemOption>).options).find(option => option.value === selectedItemId) ?? null;
  }, [options, selectedItemId]);

  return (
    <Select
      id={id ?? "itemSelector"}
      className={className ?? ""}
      value={selectedOption}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      onInputChange={(value) => setIsSearching(value !== "")}
      onChange={(option) => onChange(option?.value ?? "")}
      onKeyDown={(e) => { if (menuIsOpen) e.stopPropagation(); }}
      options={options}
      isOptionDisabled={(option) => option.isDisabled}
      menuPlacement="auto"
      maxMenuHeight={1200}
      minMenuHeight={250}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      formatOptionLabel={(data, meta) => (
        <div style={{ marginLeft: `${meta.context === "menu" ? data.level : 0}em`}} >{data.label}</div>)}
      formatGroupLabel={(group) => (
        <div style={{ paddingLeft: "6px", fontWeight: "bold", backgroundColor: groupColors[(group.label || "UNKNOWN") as keyof(typeof groupColors)] }}>{group.label}</div>
      )}
      styles={{
        container: (baseStyles) => ({
          ...baseStyles,
          fontFamily: "'Source Sans 3', 'Seravek', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
          color: "#ddd",
          borderRadius: "4px",
          flexGrow: 1,
          width: sizeAfterContent ? "fit-content" : 0,
          marginRight: "2px",
          padding: 0
        }),
        control: (baseStyles) => ({
          ...baseStyles,
          backgroundColor: "#666",
          minHeight: undefined,
          flexWrap: "nowrap"
        }),
        singleValue: (baseStyles) => ({
          ...baseStyles,
          color: "white",
          textOverflow: "initial",
          overflow: sizeAfterContent ? "initial" : "hidden"
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          padding: 0
        }),
        dropdownIndicator: (baseStyles) => ({
          ...baseStyles,
          padding: 0,
          color: "#bbb",
          ":hover": {
            color: "#fff"
          }
        }),
        indicatorSeparator: (baseStyles) => ({
          ...baseStyles,
          marginBottom: "2px",
          marginTop: "2px"
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          color: "#ddd",
          padding: 0,
          margin: 0
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
          color: "#bbb",
          fontStyle: "italic"
        }),
        menuPortal: (baseStyles) => ({
          ...baseStyles,
          zIndex: 11100,
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          width: "max-content",
          maxWidth: "250px",
          minWidth: "100%",
          backgroundColor: "#505050",
          border: "1px solid #111",
          marginTop: 0,
          borderRadius: "0px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.5)"
        }),
        option: (baseStyles, { isSelected, isFocused, isDisabled }) => ({
          ...baseStyles,
          backgroundColor: isSelected ? "#333" : (isFocused ? "#202020" : "#505050"),
          color: isDisabled ? "#888" : "#eee",
          ":hover": {
            backgroundColor: isDisabled ? "#505050" : "#000"
          },
          padding: "2px 6px",
          cursor: "pointer"
        }),
        groupHeading: (baseStyles) => ({
          ...baseStyles,
          backgroundColor: "#505050",
          color: "#333",
          margin: "0px",
          padding: "0px"
        }),
        menuList: (baseStyles) => ({
          ...baseStyles
        })
      }}
    />
  );
}

export default ItemSelector;